import { getCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Link, { StyledIcon } from "@insite/mobius/Link";
import Menu from "@insite/mobius/Menu";
import { MainNavigationStyles, MappedLink } from "@tCooneyBrothers/Overrides/Widgets/Header/MainNavigation";
import * as React from "react";
import { ThemeProps, withTheme } from "styled-components";

import { setMainNavigation } from "@insite/client-framework/Components/ShellHoleConnect";
import Icon from "@insite/mobius/Icon";
import X from "@insite/mobius/Icons/X";

interface MainNavigationItemProps {
    link: MappedLink;
    index: number;
    styles: MainNavigationStyles;
    container: React.RefObject<HTMLElement>;
    displayChangeCustomerLink: boolean;
}

interface State {
    hover: boolean;
}

class MainNavigationItem extends React.Component<ThemeProps<BaseTheme> & MainNavigationItemProps, State> {
    element = React.createRef<HTMLElement>();
    popover = React.createRef<any>();

    constructor(props: ThemeProps<BaseTheme> & MainNavigationItemProps) {
        super(props);
        this.state = {
            hover: false,
        };
    }

    UNSAFE_componentWillMount(): void {
        setMainNavigation({
            close: () => {
                this.closePopover();
            },
            openMenu: (index: number) => {
                this.openPopover();
            },
        });
    }

    closePopover = () => {
        this.popover.current && (this.popover.current! as any).closePopover();
    };

    openPopover = () => {
        this.popover.current && (this.popover.current! as any).openPopover();
    };

    addToDataLayer = (index: number, title: string) => {
        this.closePopover();
        this.hoverOff();
        const dataLayer = (window as any).dataLayer || [];

        const categoryTitle = title;

        if (dataLayer) {
            dataLayer.push({ ecommerce: null });
            dataLayer.push({
                event: "drop_down_category_clicks",
                ecommerce: {
                    category_clicks: categoryTitle || "",
                },
            });
        }
        return dataLayer;
    };

    hoverOn = () => {
        this.setState({ hover: true });
    };
    hoverOff = () => {
        this.setState({ hover: false });
    };

    // Check Screen Resolution than give height to main content

    checkResolution() {
        // eslint-disable-next-line no-restricted-globals
        const physicalScreenHeight = screen.height * window.devicePixelRatio;
        if (physicalScreenHeight <= 768) {
            const mainDiv = document.querySelector<HTMLElement>(`main`);
            if (mainDiv) {
                mainDiv.style.minHeight = "600px";
            }
        } else if (physicalScreenHeight >= 768) {
            const mainDiv = document.querySelector<HTMLElement>(`main`);
            if (mainDiv) {
                mainDiv.style.minHeight = "670px";
            }
        }
    }

    componentDidUpdate() {
        this.checkResolution();
    }

    componentDidMount() {
        document.addEventListener("click", this.handleDocumentClick);
    }

    componentWillUnmount() {
        document.removeEventListener("click", this.handleDocumentClick);
    }

    handleDocumentClick = (event: MouseEvent) => {
        let target = event.target as HTMLElement | null;

        while (target) {
            const hasMegaMenuId = target.id === "megaMenuId";
            const hasItemWrapperClass = target.classList?.contains("item-wrapper");

            if (hasMegaMenuId || hasItemWrapperClass) {
                return;
            }

            target = target.parentElement;
        }
        this.hoverOff();
    };

    render() {
        const {
            styles,
            link,
            index,
            theme: {
                breakpoints: { maxWidths },
            },
            displayChangeCustomerLink,
            container,
        } = this.props;
        const { hover } = this.state;
        const downIcon = <StyledIcon src={ChevronDown} {...styles.menuItemIcon} />;
        const hasChildren = link.children && link.children.filter(child => !child.excludeFromNavigation).length > 0;
        const isCascading = hasChildren && link.childrenType === "CascadingMenu";
        const isMega = hasChildren && link.childrenType === "MegaMenu";

        let menuLink = (
            <Link
                className="menu-item"
                typographyProps={styles.menuItemTypography}
                color={styles.menuItemTypography?.color}
                {...styles.menuItem}
                href={link.url}
                target={link.openInNewWindow ? "_blank" : ""}
            >
                {link.title}
                {isCascading && downIcon}
            </Link>
        );
        if (isMega || link.url === "") {
            menuLink = (
                <Link
                    className="menu-item"
                    typographyProps={styles.menuItemTypography}
                    color={styles.menuItemTypography?.color}
                    {...styles.menuItem}
                >
                    {link.title}
                    {(isCascading || isMega) && downIcon}
                </Link>
            );
        }

        let menuItem = menuLink;

        if (isCascading && link.children) {
            const menuProps: { maxDepth?: number } = {};
            if (link.maxDepth) {
                menuProps.maxDepth = link.maxDepth;
            }

            menuItem = (
                <Menu
                    descriptionId={`${link.title}_${index}`}
                    menuItems={link.children}
                    menuTrigger={menuLink}
                    displayChangeCustomerLink={displayChangeCustomerLink}
                    {...menuProps}
                    {...styles.cascadingMenu}
                />
            );
        }
        if (isMega) {
            menuItem = (
                <StyledWrapper {...styles.megaMenuWrapper} className="mega-menu">
                    <Link
                        typographyProps={styles.menuItemTypography}
                        color={styles.menuItemTypography?.color}
                        {...styles.menuItem}
                        onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();

                            this.setState(prev => ({ hover: !prev.hover }));
                        }}
                    >
                        {link.title}
                        {(isCascading || isMega) && downIcon}
                    </Link>
                    <StyledWrapper
                        {...(hover ? { ...styles.openMegaMenu } : { ...styles.closeMegaMenu })}
                        data-test-selector="megaMenu"
                        id="megaMenuId"
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                        <Icon src={X} size={20} {...styles.crossIcon} onClick={this.hoverOff} />
                        <GridContainer
                            className="mega-menu-grid-container"
                            {...styles.megaMenuGridContainer}
                            offsetProps={{ ...styles.megaMenuGridContainer?.offsetProps, as: "ul" }}
                        >
                            {link
                                .children!.filter(child => !child.excludeFromNavigation)
                                .map((child, index) => (
                                    <GridItem
                                        className="mega-menu-grid-item"
                                        width={(12 / link.numberOfColumns!) as GridWidths}
                                        key={index}
                                        {...styles.megaMenuGridItem}
                                        as="li"
                                    >
                                        <Link
                                            {...styles.megaMenuHeading}
                                            href={child.url}
                                            // onClick={this.hoverOff}
                                            className="mega-menu-heading-link"
                                            onClick={() => this.addToDataLayer(index, child.title)}
                                        >
                                            {child.title}
                                        </Link>
                                        {child.children && (
                                            <ul data-test-selector={`linksFor_${child.title}`}>
                                                {child.children
                                                    .filter(grandChild => {
                                                        if (grandChild.excludeFromNavigation) {
                                                            return false;
                                                        }
                                                        if (
                                                            grandChild.type === "ChangeCustomerPage" &&
                                                            !displayChangeCustomerLink
                                                        ) {
                                                            return false;
                                                        }
                                                        if (grandChild.type === "VmiUsersPage") {
                                                            const currentMode = getCookie(NavigationModeCookieName);
                                                            if (!currentMode || currentMode === "Storefront") {
                                                                return false;
                                                            }
                                                        }
                                                        return true;
                                                    })

                                                    .map((grandChild, index) => (
                                                        <li key={index}>
                                                            <Link
                                                                className="mega-menu-link"
                                                                {...styles.megaMenuLink}
                                                                // onClick={this.hoverOff}
                                                                onClick={() =>
                                                                    this.addToDataLayer(index, grandChild.title)
                                                                }
                                                                href={grandChild.url}
                                                                data-test-selector={grandChild.title}
                                                            >
                                                                {grandChild.title}
                                                            </Link>
                                                        </li>
                                                    ))}
                                            </ul>
                                        )}
                                    </GridItem>
                                ))}
                        </GridContainer>
                    </StyledWrapper>
                </StyledWrapper>
            );
        }

        return menuItem;
    }
}

export default withTheme(MainNavigationItem);
